import { toast } from "$global-providers/toast-provider";
import { gql, useMutation } from "@apollo/client";
import dayjs from "dayjs";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import type { InventoryTableFilterState } from "./inventory-filter-state";
import { INVENTORY_SEARCH_QUERY } from "./queries";

const UPDATE_LOT_MUTATION = gql`
  mutation updateLot(
    $id: ID!
    $facilityId: ID!
    $expiryDate: Date!
    $quantity: Int!
    $reasonForChange: ChangeReason!
    $reasonForChangeText: String
  ) {
    updateLot(
      input: {
        id: $id
        facilityId: $facilityId
        expiryDate: $expiryDate
        quantity: $quantity
        reasonForChange: $reasonForChange
        reasonForChangeText: $reasonForChangeText
      }
    ) {
      id
      quantity
      expiryDate
    }
  }
`;

export function useUpdateLot({
  facilityId,
  onUpdateSuccess,
  inventoryTableFilterState,
  pageSize,
  currentPage,
}: {
  facilityId: string;
  onUpdateSuccess: () => void;
  inventoryTableFilterState: InventoryTableFilterState;
  pageSize: number;
  currentPage: number;
}) {
  const { formatMessage } = useIntl();

  const [updateLot] = useMutation(UPDATE_LOT_MUTATION, {
    onError(err) {
      const uniqueConstraintError = err.message.match(
        /Unique constraint failed on the fields/g,
      )?.length;

      toast.error(
        uniqueConstraintError
          ? formatMessage({
              id: "inventory.overview.updateLotError.expiryDateUniqueConstraint",
              defaultMessage:
                "Cannot update: A lot with this expiry date already exists",
            })
          : formatMessage({
              id: "inventory.overview.updateLotError",
              defaultMessage: "Something went wrong while updating the lot",
            }),
        { duration: 5000 },
      );
    },
    onCompleted() {
      toast.success(
        formatMessage({
          id: "inventory.overview.updateLotSuccess",
          defaultMessage: "Successfully updated the lot",
        }),
      );
      onUpdateSuccess();
    },
    refetchQueries: [
      {
        query: INVENTORY_SEARCH_QUERY,
        variables: {
          facilityId,
          locationId: inventoryTableFilterState.locationId,
          name: inventoryTableFilterState.searchTerm,
          orderBy:
            inventoryTableFilterState.sortBy?.field ??
            inventoryTableFilterState.defaultInventorySortBy?.field,
          orderDirection: inventoryTableFilterState.sortBy?.direction,
          pageNumber: currentPage,
          pageSize,
        },
      },
    ],
  });

  return useCallback(
    async (data) => {
      await updateLot({
        variables: {
          id: data.id,
          quantity: data.quantity,
          expiryDate: dayjs(data.expiryDate)
            .endOf("month")
            .format("YYYY-MM-DD"),
          reasonForChange: data.reasonCategory,
          reasonForChangeText: data.reasonText || null,
          facilityId,
        },
      });
    },
    [updateLot, facilityId],
  );
}
